import React, { useState, useEffect, useCallback } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import {
  Globe,
  Phone,
  Sparkles,
  Mail,
  ChevronDown,
  LogOut,
  User,
  Bell,
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { homePathForRole } from '../../utils/roleHome';
import {
  ServicesNavDropdown,
  ServicesNavDrawer,
  InterpretingNavDropdown,
  InterpretingNavDrawer,
} from './ServicesNavDropdown';
import assetUrl from '../../assetUrl';
import { publicApi, adminApi, clientApi, translatorApi, verifierApi } from '../../api/client';
import './Header.css';

const NOTIF_API = {
  client: clientApi,
  translator: translatorApi,
  admin: adminApi,
  lawyer: verifierApi,
  notary: verifierApi,
};
const NOTIF_PATH = {
  client: '/app/notifications',
  translator: '/pro/notifications',
  admin: '/admin/notifications',
  lawyer: '/lawyer/notifications',
  notary: '/notary/notifications',
};

const languages = [
  { code: 'fr', label: 'FR' },
  { code: 'ar', label: 'AR' },
  { code: 'en', label: 'EN' },
];

const navLinkMeta = [
  { to: '/services', labelKey: 'translation', hasDropdown: true, dropdown: 'translation' },
  { to: '/services?type=interpreting', labelKey: 'interpreting', hasDropdown: true, dropdown: 'interpreting' },
  { to: '/about', labelKey: 'about' },
  { to: '/contact', labelKey: 'contact' },
  { to: '/annuaire', labelKey: 'annuaire', label: 'Annuaire' },
  { to: '/devenir-traducteur', labelKey: 'translator', highlight: 'nav-translator' },
];

export default function Header() {
  const { t, lang, setLang, isRTL } = useLanguage();
  const { isAuthenticated, user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [openMobileDropdown, setOpenMobileDropdown] = useState(null);
  const [publicSettings, setPublicSettings] = useState({});
  const [notifs, setNotifs] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const navigate = useNavigate();

  const notifPath = user ? NOTIF_PATH[user.role] : null;
  const unreadCount = notifs.filter((n) => !n.readAt).length;

  useEffect(() => {
    const apiObj = isAuthenticated ? NOTIF_API[user?.role] : null;
    if (!apiObj?.notifications) return undefined;
    let mounted = true;
    const load = () => {
      apiObj
        .notifications()
        .then((res) => {
          const list = Array.isArray(res) ? res : res?.notifications;
          if (mounted) setNotifs(Array.isArray(list) ? list : []);
        })
        .catch(() => { if (mounted) setNotifs([]); });
    };
    load();
    const timer = setInterval(load, 15000);
    return () => { mounted = false; clearInterval(timer); };
  }, [isAuthenticated, user?.role]);

  useEffect(() => {
    if (!notifOpen) return undefined;
    const onPointerDown = (e) => {
      if (!e.target.closest('.header-notif-wrapper')) setNotifOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [notifOpen]);

  const openNotification = async (n) => {
    setNotifOpen(false);
    const apiObj = NOTIF_API[user?.role];
    try {
      await apiObj?.readNotifications?.();
    } catch {
      /* ignore */
    }
    setNotifs((prev) => prev.map((x) => (x.readAt ? x : { ...x, readAt: new Date().toISOString() })));
    navigate(n.link || notifPath || '/');
  };

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    setOpenMobileDropdown(null);
  }, []);

  const closeDropdown = useCallback(() => setOpenDropdown(null), []);
  const closeUserMenu = useCallback(() => setUserMenuOpen(false), []);

  // « Commander en ligne » : visible pour les visiteurs ; pour un utilisateur
  // connecté, uniquement si son rôle est « client ».
  const canOrder = !isAuthenticated || user?.role === 'client';

  // « Devenir traducteur » n'est visible que pour les visiteurs non connectés
  const navLinks = navLinkMeta
    .filter((item) => !(isAuthenticated && item.to === '/devenir-traducteur'))
    .map((item) => ({
      ...item,
      label: item.label || t.nav[item.labelKey],
    }));

  const servicesMenu = t.nav.servicesMenu;
  const translationLabels = { viewAll: t.nav.viewAllServices };
  const interpretingLabels = { viewAll: t.nav.viewAllInterpreting };

  const dropdownConfig = {
    translation: { Panel: ServicesNavDropdown, Drawer: ServicesNavDrawer, labels: translationLabels },
    interpreting: { Panel: InterpretingNavDropdown, Drawer: InterpretingNavDrawer, labels: interpretingLabels },
  };

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeMenu();
        setLangOpen(false);
        closeDropdown();
        closeUserMenu();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [closeMenu, closeDropdown, closeUserMenu]);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 1100) closeMenu();
      else closeDropdown();
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [closeMenu, closeDropdown]);

  useEffect(() => {
    let mounted = true;
    publicApi
      .settings()
      .then((res) => {
        if (mounted && res?.settings) setPublicSettings(res.settings);
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!openDropdown) return undefined;

    const onPointerDown = (e) => {
      if (!e.target.closest('.nav-services-wrapper')) {
        closeDropdown();
      }
    };

    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [openDropdown, closeDropdown]);

  useEffect(() => {
    if (!userMenuOpen) return undefined;

    const onPointerDown = (e) => {
      if (!e.target.closest('.header-user-wrapper')) {
        closeUserMenu();
      }
    };

    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [userMenuOpen, closeUserMenu]);

  const renderDesktopDropdown = (link) => {
    const isOpen = openDropdown === link.dropdown;
    const config = dropdownConfig[link.dropdown] || dropdownConfig.translation;
    const Dropdown = config.Panel;

    return (
      <div
        key={link.labelKey}
        className={`nav-services-wrapper${isOpen ? ' open' : ''}`}
      >
        <button
          type="button"
          className="nav-services-trigger"
          aria-expanded={isOpen}
          aria-haspopup="true"
          onClick={(e) => {
            e.stopPropagation();
            setOpenDropdown((current) => (current === link.dropdown ? null : link.dropdown));
          }}
        >
          {link.label}
          <ChevronDown size={14} className="nav-services-chevron" aria-hidden="true" />
        </button>
        {isOpen && (
          <div className="nav-services-dropdown">
            <Dropdown
              menu={servicesMenu}
              labels={config.labels}
              onNavigate={closeDropdown}
            />
          </div>
        )}
      </div>
    );
  };

  const renderDesktopLink = (link) => {
    if (link.hasDropdown) return renderDesktopDropdown(link);

    return link.to.startsWith('/#') ? (
      <a key={link.labelKey} href={link.to} className={link.highlight || ''}>
        {link.label}
      </a>
    ) : (
      <NavLink
        key={link.labelKey}
        to={link.to}
        end={link.end}
        className={({ isActive }) => `${link.highlight || ''}${isActive ? ' active' : ''}`.trim()}
      >
        {link.label}
      </NavLink>
    );
  };

  const renderDrawerLink = (link) => {
    if (link.hasDropdown) {
      const isOpen = openMobileDropdown === link.dropdown;
      const config = dropdownConfig[link.dropdown] || dropdownConfig.translation;
      const Drawer = config.Drawer;

      return (
        <div key={link.labelKey} className={`nav-drawer-services${isOpen ? ' open' : ''}`}>
          <button
            type="button"
            className="nav-drawer-link nav-drawer-services-trigger"
            aria-expanded={isOpen}
            onClick={() => setOpenMobileDropdown((current) => (current === link.dropdown ? null : link.dropdown))}
          >
            <span className="nav-drawer-link-text">{link.label}</span>
            <ChevronDown size={16} className="nav-drawer-services-chevron" aria-hidden="true" />
          </button>
          {isOpen && (
            <Drawer
              menu={servicesMenu}
              labels={config.labels}
              onNavigate={closeMenu}
            />
          )}
        </div>
      );
    }

    const content = <span className="nav-drawer-link-text">{link.label}</span>;

    if (link.to.startsWith('/#')) {
      return (
        <a
          key={link.labelKey}
          href={link.to}
          className={`nav-drawer-link ${link.highlight || ''}`.trim()}
          onClick={closeMenu}
        >
          {content}
        </a>
      );
    }

    return (
      <NavLink
        key={link.labelKey}
        to={link.to}
        end={link.end}
        className={({ isActive }) => `nav-drawer-link ${link.highlight || ''}${isActive ? ' active' : ''}`.trim()}
        onClick={closeMenu}
      >
        {content}
      </NavLink>
    );
  };

  return (
    <header className={`header ${isRTL ? 'rtl' : ''} ${menuOpen ? 'menu-open' : ''}`}>
      <div className="header-top">
        <div className="container header-top-inner">
          <div className="header-top-contacts">
            <a href={`tel:${(publicSettings.contactPhone || '+216 71 234 567').replace(/\s/g, '')}`} className="header-phone">
              <Phone size={14} />
              <span>{publicSettings.contactPhone || '+216 71 234 567'}</span>
            </a>
            <a href={`mailto:${publicSettings.contactEmail || 'contact@tarjemlek.tn'}`} className="header-email">
              <Mail size={14} />
              <span>{publicSettings.contactEmail || 'contact@tarjemlek.tn'}</span>
            </a>
          </div>
          <p className="header-top-message">
            <Sparkles size={13} className="header-top-message-icon" aria-hidden="true" />
            <span className="header-top-message-text">{t.nav.topMessage}</span>
          </p>
          <div className={`header-lang-wrapper header-lang-desktop${langOpen ? ' open' : ''}`}>
            <button
              type="button"
              className="header-lang-btn"
              onClick={() => setLangOpen(!langOpen)}
              aria-expanded={langOpen}
              aria-label="Change language"
            >
              <Globe size={16} />
              {lang.toUpperCase()}
            </button>
            {langOpen && (
              <>
                <button
                  type="button"
                  className="header-lang-backdrop"
                  aria-label="Close language menu"
                  onClick={() => setLangOpen(false)}
                />
                <div className="header-lang-dropdown">
                  {languages.map((l) => (
                    <button
                      key={l.code}
                      type="button"
                      className={lang === l.code ? 'active' : ''}
                      onClick={() => { setLang(l.code); setLangOpen(false); }}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="header-main">
        <div className="container header-inner">
          <Link to="/" className="logo" onClick={closeMenu}>
            <img
              src={assetUrl('/images/tarjemlek-logo-transparent.png')}
              alt={publicSettings.siteName || 'Tarjemlek — ترجملك'}
              className="logo-img"
            />
          </Link>

          <nav className="nav nav-desktop" aria-label="Main navigation">
            {navLinks.map(renderDesktopLink)}
          </nav>

          <div className="header-actions">
            {canOrder && (
              <Link to="/order" className="header-order-cta">
                {t.nav.order}
              </Link>
            )}

            {isAuthenticated && notifPath && (
              <div className={`header-notif-wrapper${notifOpen ? ' open' : ''}`}>
                <button
                  type="button"
                  className="header-notif-btn"
                  onClick={() => setNotifOpen((open) => !open)}
                  aria-expanded={notifOpen}
                  aria-haspopup="true"
                  aria-label="Notifications"
                  title="Notifications"
                >
                  <Bell size={19} />
                  {unreadCount > 0 && (
                    <span className="header-notif-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
                  )}
                </button>
                {notifOpen && (
                  <div className="header-notif-dropdown">
                    <div className="header-notif-dropdown-head">Notifications</div>
                    <div className="header-notif-dropdown-list">
                      {notifs.length === 0 ? (
                        <p className="header-notif-empty">—</p>
                      ) : (
                        notifs.slice(0, 6).map((n) => (
                          <button
                            key={n.id}
                            type="button"
                            className={`header-notif-item${n.readAt ? '' : ' is-unread'}`}
                            onClick={() => openNotification(n)}
                          >
                            <strong>{n.title}</strong>
                            {n.body && <p>{n.body}</p>}
                            <small>{n.createdAt ? new Date(n.createdAt).toLocaleString() : ''}</small>
                          </button>
                        ))
                      )}
                    </div>
                    <Link to={notifPath} className="header-notif-more" onClick={() => setNotifOpen(false)}>
                      Tout voir
                    </Link>
                  </div>
                )}
              </div>
            )}

            {isAuthenticated ? (
              <div className={`header-user-wrapper${userMenuOpen ? ' open' : ''}`}>
                <button
                  type="button"
                  className="header-user-btn"
                  onClick={() => setUserMenuOpen((open) => !open)}
                  aria-expanded={userMenuOpen}
                  aria-haspopup="true"
                  aria-label={t.auth.myAccount}
                  title={t.auth.myAccount}
                >
                  <User size={20} />
                  <ChevronDown size={14} className="header-user-chevron" aria-hidden="true" />
                </button>
                {userMenuOpen && (
                  <div className="header-user-dropdown">
                    <Link
                      to={homePathForRole(user?.role)}
                      onClick={closeUserMenu}
                    >
                      <User size={16} />
                      <span>{t.auth.dashboard}</span>
                    </Link>
                    <button
                      type="button"
                      className="header-user-dropdown-logout"
                      onClick={() => { logout(); closeUserMenu(); }}
                    >
                      <LogOut size={16} />
                      <span>{t.auth.logout}</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login" className="header-login" aria-label={t.nav.login} title={t.nav.login}>
                <User size={20} />
              </Link>
            )}

            <button
              type="button"
              className={`menu-toggle ${menuOpen ? 'open' : ''}`}
              onClick={() => setMenuOpen(!menuOpen)}
              aria-expanded={menuOpen}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            >
              <span className="menu-toggle-bars" aria-hidden="true">
                <span />
                <span />
                <span />
              </span>
            </button>
          </div>
        </div>
      </div>

      <button
        type="button"
        className={`nav-overlay ${menuOpen ? 'open' : ''}`}
        aria-label="Close menu"
        onClick={closeMenu}
        tabIndex={menuOpen ? 0 : -1}
      />

      <div
        className={`nav-drawer ${menuOpen ? 'open' : ''}`}
        aria-hidden={!menuOpen}
        aria-label={t.nav.menu}
      >
        <nav className="nav-drawer-links" aria-label="Mobile navigation">
          {navLinks.map(renderDrawerLink)}
        </nav>

        <div className="nav-drawer-footer">
          {canOrder && (
            <Link to="/order" className="btn btn-primary nav-drawer-order" onClick={closeMenu}>
              {t.nav.order}
            </Link>
          )}

          {!isAuthenticated ? (
            <Link to="/login" className="btn btn-outline nav-drawer-login" onClick={closeMenu}>
              <User size={18} />
              {t.nav.login}
            </Link>
          ) : (
            <div className="nav-drawer-user">
              <Link to={homePathForRole(user?.role)} onClick={closeMenu}>
                <User size={16} />
                <span>{user?.name}</span>
              </Link>
              {notifPath && (
                <Link to={notifPath} className="nav-drawer-notif" onClick={closeMenu} aria-label="Notifications">
                  <Bell size={16} />
                  {unreadCount > 0 && <span className="header-notif-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>}
                </Link>
              )}
              <button type="button" onClick={() => { logout(); closeMenu(); }} aria-label={t.auth.logout}>
                <LogOut size={16} />
              </button>
            </div>
          )}

          <div className="nav-drawer-lang">
            <span className="nav-drawer-lang-label">
              <Globe size={16} />
              {t.nav.language}
            </span>
            <div className="nav-drawer-lang-pills">
              {languages.map((l) => (
                <button
                  key={l.code}
                  type="button"
                  className={lang === l.code ? 'active' : ''}
                  onClick={() => setLang(l.code)}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>

          <div className="nav-drawer-contacts">
            <a href={`tel:${(publicSettings.contactPhone || '+216 71 234 567').replace(/\s/g, '')}`} className="nav-drawer-contact">
              <Phone size={16} />
              <span>{publicSettings.contactPhone || '+216 71 234 567'}</span>
            </a>
            <a href={`mailto:${publicSettings.contactEmail || 'contact@tarjemlek.tn'}`} className="nav-drawer-contact nav-drawer-email">
              <Mail size={16} />
              <span>{publicSettings.contactEmail || 'contact@tarjemlek.tn'}</span>
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}
